import React from 'react';
import { useCartSuggestions } from '@/hooks/useCartSuggestions';
import { useCart } from '@/contexts/CartContext';
import { useMenuTheme } from '@/hooks/useMenuTheme';
import { useTranslation } from 'react-i18next';
import { Plus, Utensils } from 'lucide-react';
import { formatCurrency } from '@/utils/currencyUtils';
import { MenuItem } from '@/types';
import { cn } from '@/lib/utils';

interface CartSuggestionsProps {
  branchId: string;
}

export const CartSuggestions: React.FC<CartSuggestionsProps> = ({ branchId }) => {
  const { data: suggestions, isLoading } = useCartSuggestions(branchId);
  const { addItem } = useCart();
  const { t } = useTranslation();
  const { branch } = useMenuTheme();

  if (isLoading || !suggestions || suggestions.length === 0) return null;

  const handleAdd = (item: MenuItem) => {
    if (item.variants && item.variants.length > 0) {
      // Add the lowest price variant by default
      const defaultVariant = [...item.variants].sort((a, b) => a.price - b.price)[0];
      addItem(item, defaultVariant, 1);
    } else {
      addItem(item, undefined, 1);
    }
  };

  return (
    <div className="mt-6 pt-4 border-t border-dashed">
      <h3 className="text-sm font-semibold mb-3 px-1 text-muted-foreground">
        {t('cart.suggestions_title', 'These items pair well with your current order.')}
      </h3>
      
      <div className="grid grid-cols-2 gap-2 px-1">
        {suggestions.map((item) => (
          <div 
            key={item.id} 
            className="flex flex-col border rounded-xl p-2 shadow-sm bg-card hover:shadow-md transition-shadow cursor-pointer group"
            onClick={() => handleAdd(item)}
          >
            {item.image_url && (
              <div className="relative aspect-[4/3] mb-2 overflow-hidden rounded-lg">
                <img 
                  src={item.image_url} 
                  alt={item.name} 
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
            )}

            <div className="flex flex-col flex-grow justify-between gap-2">
              <h4 className="text-xs font-medium line-clamp-2 leading-tight text-foreground/90">
                {item.name}
              </h4>
              
              <div className="flex items-center justify-between mt-auto">
                <div className="text-xs font-bold text-primary">
                  {formatCurrency(item.base_price, branch?.currency || 'USD')}
                </div>
                <div 
                  className="h-6 w-6 rounded-full bg-secondary flex items-center justify-center text-foreground transition-colors group-hover:bg-primary group-hover:text-primary-foreground"
                >
                  <Plus size={14} strokeWidth={2} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
